<template>
  <div class="qilin-page">
    <headerSection v-if="!standalonePreview" />
    <main class="qilin-page__main">
      <div class="qilin-page__top">
        <router-link to="/" aria-label="กลับหน้าหลัก">←</router-link>
        <div><small>คู่หูนำโชค</small><h1>คู่หูของฉัน</h1></div>
        <button v-if="pet" type="button" @click="openProfile">✎ ชื่อ</button>
      </div>

      <div v-if="loading" class="qilin-page__state">กำลังพาคู่หูมาหา...</div>
      <div v-else-if="error" class="qilin-page__state qilin-page__state--error">
        <strong>{{ error }}</strong><router-link to="/">กลับหน้าหลัก</router-link>
      </div>
      <template v-else-if="pet">
        <div v-if="notice" class="qilin-notice">{{ notice }}</div>
        <div class="qilin-pet-grid">
          <QilinAvatar :pet="displayPet" :loading="playing" @play="play" @pet-tap="petTapCount += 1" />
          <aside class="qilin-pet-panel">
            <div class="qilin-pet-panel__name">
              <span>{{ pet.gender === 'female' ? '♀' : pet.gender === 'male' ? '♂' : '✦' }}</span>
              <div><small>ชื่อคู่หูของคุณ</small><h2>{{ pet.name || 'ยังไม่มีชื่อ' }}</h2></div>
            </div>
            <QilinProgress :pet="pet" />
            <div class="qilin-pet-panel__stats">
              <QilinStatusStrip icon="🍚" title="ให้อาหาร" :detail="pet.lastFedDate ? `ล่าสุด ${pet.lastFedDate}` : 'ยังไม่เคยให้อาหาร'" />
              <QilinStatusStrip icon="♡" title="เล่นด้วยกัน" :detail="pet.playedToday ? 'เล่นแล้ววันนี้' : 'รับได้วันละ 5 XP'" />
              <QilinStatusStrip icon="✨" title="จำนวนครั้งที่ให้อาหาร" :detail="`${pet.feedCount || 0} ครั้ง`" />
            </div>
            <div class="qilin-pet-panel__links">
              <router-link :to="previewRoute('/checkin')"><b>เช็กอินให้อาหาร</b><span>ฝากครบ 100 บาท รับ 10 XP และเหรียญ ›</span></router-link>
              <router-link :to="previewRoute('/missions')"><b>ภารกิจ</b><span>ดูเป้าหมายรายวันและรายสัปดาห์ ›</span></router-link>
            </div>
          </aside>
        </div>
        <QilinEvolutionGallery :pet="pet" />
      </template>
    </main>
    <footerSection v-if="!standalonePreview" />
    <QilinProfileModal
      :visible="showProfile"
      :pet="pet || {}"
      :saving="saving"
      :error="profileError"
      @close="closeProfile"
      @save="saveProfile"
    />
  </div>
</template>

<script>
import qilinApi, { qilinErrorMessage, qilinPreviewEnabled } from '../services/qilinRetention'
import QilinAvatar from '../components/qilin/QilinAvatar.vue'
import QilinEvolutionGallery from '../components/qilin/QilinEvolutionGallery.vue'
import QilinProgress from '../components/qilin/QilinProgress.vue'
import QilinProfileModal from '../components/qilin/QilinProfileModal.vue'
import QilinStatusStrip from '../components/qilin/QilinStatusStrip.vue'

export default {
  name: 'QilinPet',
  components: {
    QilinAvatar,
    QilinEvolutionGallery,
    QilinProgress,
    QilinProfileModal,
    QilinStatusStrip,
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue')
  },
  data () {
    return { pet: null, loading: true, playing: false, saving: false, showProfile: false, notice: '', error: '', profileError: '', petTapCount: 0 }
  },
  computed: {
    standalonePreview () {
      return qilinPreviewEnabled && this.$route.query.preview === 'standalone'
    },
    moodOverride () {
      const mood = String(this.$route.query.mood || '').toLowerCase()
      return ['new', 'happy', 'content', 'hungry', 'sulking'].indexOf(mood) >= 0 ? mood : ''
    },
    displayPet () {
      if (!this.pet || !this.moodOverride) return this.pet
      return Object.assign({}, this.pet, { mood: this.moodOverride })
    }
  },
  mounted () {
    this.installPreviewTestHooks()
    this.load()
  },
  beforeDestroy () {
    if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') return
    if (window.render_game_to_text === this.renderPreviewState) delete window.render_game_to_text
    if (window.advanceTime === this.advancePreviewTime) delete window.advanceTime
  },
  methods: {
    installPreviewTestHooks () {
      if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') return
      window.render_game_to_text = this.renderPreviewState
      window.advanceTime = this.advancePreviewTime
    },
    renderPreviewState () {
      return JSON.stringify({
        mode: 'qilin-pet',
        pet: this.displayPet,
        ui: {
          loading: this.loading,
          playing: this.playing,
          profileOpen: this.showProfile,
          notice: this.notice,
          moodOverride: this.moodOverride,
          petTapCount: this.petTapCount
        }
      })
    },
    advancePreviewTime () { return this.renderPreviewState() },
    async load () {
      this.loading = true
      try {
        const response = await qilinApi.getPetStatus()
        this.pet = response.pet
        this.showProfile = !this.pet.name || !this.pet.gender
      } catch (error) {
        this.error = qilinErrorMessage(error)
      } finally { this.loading = false }
    },
    async play () {
      this.playing = true
      this.notice = ''
      try {
        const response = await qilinApi.play()
        this.pet = response.pet
        const extras = response.coinBonus ? ` และโบนัสเลเวล ${response.coinBonus} เหรียญ` : ''
        this.notice = `เล่นด้วยกันแล้ว รับ 5 XP${extras}`
      } catch (error) { this.notice = qilinErrorMessage(error) } finally { this.playing = false }
    },
    requestId () { return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}` },
    previewRoute (path) {
      return this.standalonePreview ? { path, query: { preview: 'standalone' } } : path
    },
    openProfile () {
      this.profileError = ''
      this.showProfile = true
    },
    closeProfile () {
      this.profileError = ''
      this.showProfile = false
    },
    async saveProfile (profile) {
      this.saving = true
      this.notice = ''
      this.profileError = ''
      try {
        const payload = Object.assign({}, profile)
        if (this.pet.name && this.pet.name !== profile.name) payload.requestId = this.requestId()
        const response = await qilinApi.updateProfile(payload)
        this.pet = response.pet
        this.showProfile = false
        this.notice = response.coinCharged ? 'เปลี่ยนชื่อสำเร็จ ใช้ 5 เหรียญ' : 'บันทึกคู่หูสำเร็จ'
      } catch (error) {
        this.profileError = qilinErrorMessage(error)
      } finally { this.saving = false }
    }
  }
}
</script>

<style scoped>
.qilin-page { min-height: 100vh; background: #09030f; font-family: 'Kanit', sans-serif; }
.qilin-page__main { width: min(1080px, calc(100% - 28px)); min-height: 76vh; margin: 0 auto; padding: 105px 0 45px; }
.qilin-page__top { display: grid; grid-template-columns: 44px 1fr auto; align-items: center; gap: 10px; margin-bottom: 18px; color: #fff; }
.qilin-page__top > a { display: grid; width: 40px; height: 40px; place-items: center; border: 1px solid rgba(255,255,255,.1); border-radius: 13px; background: rgba(255,255,255,.06); color: #fff; font-size: 20px; text-decoration: none; }
.qilin-page__top small { color: #c47cf0; font-size: 11px; font-weight: 900; letter-spacing: .08em; }
.qilin-page__top h1 { margin: 0; font-size: 25px; font-weight: 900; }
.qilin-page__top button { min-height: 39px; padding: 0 13px; border: 1px solid rgba(255,218,112,.25); border-radius: 12px; background: rgba(251,200,68,.08); color: #ffe07b; font-family: inherit; font-size: 12px; }
.qilin-page__state { min-height: 420px; display: grid; place-items: center; color: rgba(255,255,255,.65); }
.qilin-page__state--error { align-content: center; gap: 12px; text-align: center; }
.qilin-page__state--error a { color: #ffd85d; }
.qilin-notice { margin-bottom: 13px; padding: 11px 14px; border: 1px solid rgba(182,92,240,.22); border-radius: 13px; background: rgba(182,92,240,.09); color: #efd6ff; font-size: 13px; }
.qilin-pet-grid { display: grid; grid-template-columns: minmax(0, 1.16fr) minmax(330px, .84fr); gap: 18px; }
.qilin-pet-panel { display: flex; flex-direction: column; gap: 13px; padding: 20px; border: 1px solid rgba(255,255,255,.08); border-radius: 25px; background: linear-gradient(160deg, #300a49, #10051b); }
.qilin-pet-panel__name { display: flex; align-items: center; gap: 12px; color: #fff; }
.qilin-pet-panel__name > span { display: grid; width: 48px; height: 48px; place-items: center; border-radius: 15px; background: rgba(182,92,240,.12); color: #dca3ff; font-size: 23px; }
.qilin-pet-panel__name small { color: rgba(255,255,255,.5); font-size: 11px; }
.qilin-pet-panel__name h2 { margin: 2px 0 0; font-size: 22px; }
.qilin-pet-panel__stats { display: grid; gap: 8px; }
.qilin-pet-panel__links { display: grid; gap: 8px; margin-top: auto; }
.qilin-pet-panel__links a { display: block; padding: 12px 13px; border-radius: 13px; background: rgba(255,255,255,.05); color: #fff; text-decoration: none; }
.qilin-pet-panel__links b, .qilin-pet-panel__links span { display: block; }
.qilin-pet-panel__links b { font-size: 13px; }
.qilin-pet-panel__links span { margin-top: 2px; color: rgba(255,255,255,.5); font-size: 10px; }
@media (max-width: 820px) { .qilin-pet-grid { grid-template-columns: 1fr; } }
@media (max-width: 575px) { .qilin-page__main { padding-top: 24px; } .qilin-pet-panel { padding: 15px; border-radius: 20px; } }
</style>
